<template>
  <div class="min-h-screen" data-theme="teal">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="coaches" />

    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('coaches.title') }}</h1>
        <button v-if="signedIn" @click="openCreateModal" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">
          {{ t('coaches.create') }}
        </button>
      </div>

      <!-- Filters -->
      <div class="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('coaches.filter_city') }}:</label>
            <select v-model="filterCity" class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{{ t('coaches.filter_all') }}</option>
              <option v-for="city in cities" :key="city" :value="city">{{ city }}</option>
            </select>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('coaches.filter_search') }}:</label>
            <input v-model="filterSearch" type="text" :placeholder="t('coaches.filter_search_placeholder')"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 w-48 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <span class="text-xs text-gray-400 ml-auto">{{ filteredCoaches.length }} / {{ items.length }}</span>
        </div>
      </div>

      <!-- Map -->
      <div v-show="allLocations.length > 0" class="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden map-container">
        <div ref="mapContainer" style="height: 320px; width: 100%;"></div>
      </div>

      <!-- Flash -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- List -->
      <div v-if="filteredCoaches.length === 0" class="text-center py-12 text-gray-500">{{ t('coaches.no_coaches') }}</div>
      <div class="grid gap-4">
        <div v-for="coach in filteredCoaches" :key="coach.id" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition">
          <div class="flex items-start justify-between">
            <img v-if="coach.logo_url" :src="coach.logo_url" class="w-14 h-14 rounded-lg object-cover border border-gray-200 shrink-0" />
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900 mb-1">{{ coach.name }}</h3>
              <div class="flex flex-wrap gap-2 mb-2">
                <span v-if="coach.city" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{{ coach.city }}</span>
                <span v-if="coach.club" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{{ coach.club }}</span>
                <span v-if="coach.online" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">{{ t('coaches.online') }}</span>
                <span v-if="coach.pending" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{{ t('coaches.pending') }}</span>
              </div>
              <p v-if="coach.locations && coach.locations.length > 0" class="text-xs text-gray-400 mb-1">
                {{ coach.locations.map(l => l.name || t('coaches.location')).join(', ') }}
              </p>
              <p v-if="coachDescription(coach)" class="text-sm text-gray-600 mb-2">{{ coachDescription(coach) }}</p>
              <div class="flex items-center gap-3">
                <a v-if="coach.phone" :href="'tel:' + coach.phone" class="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                  {{ coach.phone }}
                </a>
                <a v-if="coach.facebook_link" :href="coach.facebook_link" target="_blank" class="hover:opacity-80 transition">
                  <svg class="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a v-if="coach.instagram_link" :href="coach.instagram_link" target="_blank" class="hover:opacity-80 transition">
                  <svg class="w-5 h-5" viewBox="0 0 24 24"><defs><linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stop-color="#feda75"/><stop offset="25%" stop-color="#fa7e1e"/><stop offset="50%" stop-color="#d62976"/><stop offset="75%" stop-color="#962fbf"/><stop offset="100%" stop-color="#4f5bd5"/></linearGradient></defs><path fill="url(#ig)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12s.014 3.668.072 4.948c.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24s3.668-.014 4.948-.072c4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948s-.014-3.667-.072-4.947c-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a :href="`/coaches/${coach.id}`" class="text-sm text-blue-700 font-medium hover:underline">{{ t('coaches.details') }} &rarr;</a>
              </div>
            </div>
            <div v-if="userAdmin || coach.user_id === userId" class="flex flex-col gap-2 ml-4">
              <button v-if="userAdmin && coach.pending" @click="approveItem(coach)" class="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition">{{ t('coaches.approve') }}</button>
              <button @click="openEditModal(coach)" class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">{{ t('coaches.edit') }}</button>
              <button @click="confirmDelete(coach)" class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">{{ t('coaches.delete') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 class="text-lg font-bold text-gray-900 mb-6">{{ editing ? t('coaches.edit_coach') : t('coaches.new_coach') }}</h2>
        <div v-if="formErrors.length" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p v-for="error in formErrors" :key="error" class="text-sm text-red-700">{{ error }}</p>
        </div>
        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('coaches.name') }}</label>
            <input v-model="form.name" type="text" required class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('coaches.club') }}</label>
            <input v-model="form.club" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('coaches.city') }}</label>
            <input v-model="form.city" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('coaches.phone') }}</label>
            <input v-model="form.phone" type="tel" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
            <input v-model="form.facebook_link" type="url" placeholder="https://facebook.com/..." class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
            <input v-model="form.instagram_link" type="url" placeholder="https://instagram.com/..." class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div class="flex items-center gap-2">
            <input v-model="form.online" type="checkbox" id="coach-online" class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <label for="coach-online" class="text-sm font-medium text-gray-700">{{ t('coaches.online') }}</label>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('coaches.description') }} (RO)</label>
            <textarea v-model="form.description_ro" rows="2" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('coaches.description') }} (RU)</label>
            <textarea v-model="form.description_ru" rows="2" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"></textarea>
          </div>

          <ImageUpload v-model="form.logo_url" :label="t('coaches.logo')" :upload-text="t('coaches.upload_logo')" :change-text="t('coaches.change_logo')" :remove-text="t('coaches.remove_logo')" :csrf-token="csrfToken" upload-type="coaches" />

          <!-- Locations -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">{{ t('coaches.locations') }}</label>
              <button type="button" @click="addLocation" class="text-xs text-blue-700 hover:text-blue-900 font-medium">+ {{ t('coaches.add_location') }}</button>
            </div>

            <div v-for="(loc, idx) in form.locations" :key="idx" class="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div class="flex items-center justify-between mb-2">
                <input v-model="loc.name" type="text" :placeholder="t('coaches.location_name')" class="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
                <button type="button" @click="removeLocation(idx)" class="ml-2 text-red-400 hover:text-red-600 text-sm">&times;</button>
              </div>
              <div :ref="el => { if (el) formMapRefs[idx] = el }" style="height: 150px; width: 100%;" class="rounded border border-gray-300 mb-2"></div>
              <div class="grid grid-cols-2 gap-2">
                <input v-model="loc.latitude" type="number" step="0.0000001" readonly :placeholder="t('coaches.latitude')" class="px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs" />
                <input v-model="loc.longitude" type="number" step="0.0000001" readonly :placeholder="t('coaches.longitude')" class="px-2 py-1 border border-gray-300 rounded bg-gray-50 text-xs" />
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button type="button" @click="closeModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('coaches.cancel') }}</button>
            <button type="submit" :disabled="submitting" class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ submitting ? '...' : (editing ? t('coaches.save') : t('coaches.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeDeleteModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ t('coaches.confirm_delete') }}</h2>
        <p class="text-sm text-gray-600 mb-6">{{ t('coaches.confirm_delete_message') }}</p>
        <div class="flex justify-end gap-3">
          <button @click="closeDeleteModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">{{ t('coaches.cancel') }}</button>
          <button @click="deleteCoach" :disabled="submitting" class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">
            {{ submitting ? '...' : t('coaches.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import ImageUpload from './ImageUpload.vue'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png', iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png' })

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, userAdmin: Boolean, coaches: Array, csrfToken: String })
const { t, locale } = useI18n()

const items = ref([...props.coaches])
const filterCity = ref('')
const filterSearch = ref('')
const showModal = ref(false)
const showDeleteModal = ref(false)
const editing = ref(false)
const editingId = ref(null)
const submitting = ref(false)
const formErrors = ref([])
const deleteTarget = ref(null)
const flash = ref(null)
const mapContainer = ref(null)
const formMapRefs = ref({})
let map = null, markers = []
let formMaps = {}
let formMarkers = {}

const form = reactive({ name: '', club: '', city: '', phone: '', facebook_link: '', instagram_link: '', online: false, description_ro: '', description_ru: '', logo_url: '', locations: [] })

const cities = computed(() => [...new Set(items.value.map(c => c.city).filter(Boolean))].sort())

const filteredCoaches = computed(() => {
  return items.value.filter(c => {
    if (filterCity.value && c.city !== filterCity.value) return false
    if (filterSearch.value) {
      const q = filterSearch.value.toLowerCase()
      if (!c.name.toLowerCase().includes(q) && !(c.club || '').toLowerCase().includes(q)) return false
    }
    return true
  })
})

const allLocations = computed(() => {
  const locs = []
  filteredCoaches.value.forEach(c => {
    if (c.locations) c.locations.forEach(l => { if (l.latitude && l.longitude) locs.push({ ...l, coach: c }) })
  })
  return locs
})

function coachDescription(coach) {
  return locale.value === 'ru' ? (coach.description_ru || coach.description_ro) : (coach.description_ro || coach.description_ru)
}

function showFlash(msg, type = 'success') { flash.value = { message: msg, type }; setTimeout(() => { flash.value = null }, 4000) }

function initMap() {
  if (!mapContainer.value || allLocations.value.length === 0) return
  if (map) { map.remove(); map = null }
  const f = allLocations.value[0]
  map = L.map(mapContainer.value).setView([parseFloat(f.latitude), parseFloat(f.longitude)], 13)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map)
  setTimeout(() => map.invalidateSize(), 100)
  updateMarkers()
}

function updateMarkers() {
  if (!map) return
  markers.forEach(m => map.removeLayer(m)); markers = []
  allLocations.value.forEach(loc => {
    const marker = L.marker([parseFloat(loc.latitude), parseFloat(loc.longitude)]).addTo(map)
    let popup = `<div style="min-width:160px"><strong>${loc.coach.name}</strong>`
    if (loc.name) popup += `<br><span style="color:#666;font-size:12px">${loc.name}</span>`
    if (loc.coach.phone) popup += `<br><a href="tel:${loc.coach.phone}" style="color:#1d4ed8;font-size:12px">${loc.coach.phone}</a>`
    popup += `<br><a href="${'/coaches/' + loc.coach.id}" style="color:#1d4ed8;font-size:12px;font-weight:500">${t('coaches.details')}</a></div>`
    marker.bindPopup(popup)
    markers.push(marker)
  })
  if (markers.length > 1) map.fitBounds(L.featureGroup(markers).getBounds().pad(0.1))
}

function addLocation() {
  form.locations.push({ id: null, name: '', latitude: '', longitude: '' })
  nextTick(() => initFormMaps())
}

function removeLocation(idx) {
  form.locations.splice(idx, 1)
  cleanupFormMaps()
}

function initFormMaps() {
  setTimeout(() => {
    form.locations.forEach((loc, idx) => {
      const el = formMapRefs.value[idx]
      if (!el || formMaps[idx]) return
      const lat = parseFloat(loc.latitude) || 47.0105
      const lng = parseFloat(loc.longitude) || 28.8638
      const fmap = L.map(el).setView([lat, lng], 13)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(fmap)
      if (loc.latitude && loc.longitude) formMarkers[idx] = L.marker([lat, lng]).addTo(fmap)
      fmap.on('click', e => {
        form.locations[idx].latitude = e.latlng.lat.toFixed(7)
        form.locations[idx].longitude = e.latlng.lng.toFixed(7)
        if (formMarkers[idx]) formMarkers[idx].setLatLng(e.latlng)
        else formMarkers[idx] = L.marker(e.latlng).addTo(fmap)
      })
      fmap.invalidateSize()
      formMaps[idx] = fmap
    })
  }, 150)
}

function cleanupFormMaps() {
  Object.keys(formMaps).forEach(k => { if (formMaps[k]) formMaps[k].remove() })
  formMaps = {}
  formMarkers = {}
  formMapRefs.value = {}
  nextTick(() => initFormMaps())
}

onMounted(() => {
  if (allLocations.value.length > 0) initMap()
  const editId = new URLSearchParams(window.location.search).get('edit')
  if (editId) { const c = items.value.find(x => x.id === parseInt(editId)); if (c) openEditModal(c) }
})
watch(allLocations, () => { nextTick(() => { if (allLocations.value.length > 0) { if (!map) initMap(); else updateMarkers() } }) })

function resetForm() {
  Object.assign(form, { name: '', club: '', city: '', phone: '', facebook_link: '', instagram_link: '', online: false, description_ro: '', description_ru: '', logo_url: '', locations: [] })
  formErrors.value = []
  cleanupFormMaps()
}

function openCreateModal() {
  editing.value = false; editingId.value = null; resetForm()
  showModal.value = true
}

function openEditModal(c) {
  editing.value = true; editingId.value = c.id
  form.name = c.name; form.club = c.club || ''; form.city = c.city || ''
  form.phone = c.phone || ''; form.facebook_link = c.facebook_link || ''; form.instagram_link = c.instagram_link || ''; form.online = c.online || false
  form.description_ro = c.description_ro || ''; form.description_ru = c.description_ru || ''; form.logo_url = c.logo_url || ''
  form.locations = (c.locations || []).map(l => ({ id: l.id, name: l.name || '', latitude: l.latitude, longitude: l.longitude }))
  formErrors.value = []
  cleanupFormMaps()
  showModal.value = true
}

function closeModal() { showModal.value = false; cleanupFormMaps() }
function confirmDelete(c) { deleteTarget.value = c; showDeleteModal.value = true }
function closeDeleteModal() { showDeleteModal.value = false; deleteTarget.value = null }

async function request(url, method, body = null) { const o = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }; if (body) o.body = JSON.stringify(body); return fetch(url, o) }

async function submitForm() {
  submitting.value = true; formErrors.value = []
  const payload = { coach: { name: form.name, club: form.club, city: form.city, phone: form.phone, facebook_link: form.facebook_link, instagram_link: form.instagram_link, online: form.online, description_ro: form.description_ro, description_ru: form.description_ru, logo_url: form.logo_url, locations: form.locations } }
  try {
    const res = editing.value ? await request(`/coaches/${editingId.value}`, 'PATCH', payload) : await request('/coaches', 'POST', payload)
    if (res.ok) {
      const d = await res.json()
      if (editing.value) { const i = items.value.findIndex(c => c.id === editingId.value); if (i !== -1) items.value[i] = d; showFlash(t('coaches.updated')) }
      else { items.value.push(d); showFlash(t('coaches.created_success')) }
      closeModal()
    } else { formErrors.value = (await res.json()).errors || [t('coaches.error')] }
  } catch { formErrors.value = [t('coaches.error')] } finally { submitting.value = false }
}

async function approveItem(coach) {
  try {
    const res = await request(`/coaches/${coach.id}/approve`, 'PATCH')
    if (res.ok) { const data = await res.json(); const idx = items.value.findIndex(c => c.id === coach.id); if (idx !== -1) items.value[idx] = data; showFlash(t('coaches.approved')) }
  } catch { showFlash(t('coaches.error'), 'error') }
}

async function deleteCoach() {
  submitting.value = true
  try { const res = await request(`/coaches/${deleteTarget.value.id}`, 'DELETE'); if (res.ok) { items.value = items.value.filter(c => c.id !== deleteTarget.value.id); showFlash(t('coaches.deleted')); closeDeleteModal() } }
  catch { showFlash(t('coaches.error'), 'error') } finally { submitting.value = false }
}
</script>
